import { Button, Checkbox, Col, Form, Input, message, Row, Tabs } from 'antd';
import { FC, useState } from 'react';
import { VerificationCodeInput } from '../VerificationCodeInput';
import { LoginType } from './type';
import {
  emailLogin,
  phoneLogin,
  sendEmailCode,
  sendMsg,
} from '../../pages/HomePage/service';
import { emailRegex, phoneRegex } from '../../utils/regex';
import { setUserInfo } from '../../utils/localStore';
import useI18n from '../../hooks/useI18n';
import { useLanguageStore } from '../../hooks';

export interface ILoginProps {
  setIsLogin: (login: boolean) => void;
}
type FieldType = {
  phone?: string;
  password?: string;
};

type EmailFieldType = {
  email?: string;
  password?: string;
};

export const Login: FC<ILoginProps> = (props) => {
  const { setIsLogin } = props;
  const [loginType, setLoginType] = useState<LoginType>('phone');
  const [phoneForm] = Form.useForm<FieldType>();
  const [emailForm] = Form.useForm<EmailFieldType>();
  const [agreeValue, setAgreeValue] = useState(false);
  const i18n = useI18n();
  const { lang } = useLanguageStore();
  const sendSMS = async () => {
    const phone = phoneForm.getFieldValue('phone');
    if (!phoneRegex.test(phone)) {
      message.info(i18n('pleaseInputCorrectPhoneNumber'));
      return Promise.reject(i18n('pleaseInputCorrectPhoneNumber'));
    }
    await sendMsg({
      content: phone,
    });
  };
  const sendEmail = async () => {
    const email = emailForm.getFieldValue('email');
    if (!emailRegex.test(email)) {
      message.info(i18n('pleaseInputCorrectEmail'));
      return Promise.reject(i18n('pleaseInputCorrectEmail'));
    }
    await sendEmailCode({
      content: email,
    });
  };
  const submitPhone = async (value: FieldType) => {
    if (!agreeValue) {
      message.info(i18n('checkAgreement'));
      return;
    }
    const result = await phoneLogin({
      content: value.phone!,
      code: value.password!,
      lang: lang,
      channel: 3,
    });
    setUserInfo(result.data.data);
    setIsLogin(true);
  };
  const submitEmail = async (value: EmailFieldType) => {
    if (!agreeValue) {
      message.info(i18n('checkAgreement'));
      return;
    }
    const result = await emailLogin({
      content: value.email!,
      code: value.password!,
      lang: lang,
      channel: 3,
    });
    setUserInfo(result.data.data);

    setIsLogin(true);
  };
  return (
    <div className="py-7 px-6 flex gap-8 flex-col">
      <div className="text-[#04252B] text-2xl">{i18n('welcomeToCarbonAI')}</div>
      <div>
        <Tabs
          activeKey={loginType}
          onChange={(activeKey) => setLoginType(activeKey as LoginType)}
        >
          <Tabs.TabPane key={'phone'} tab={i18n('loginByMobile')} />
          <Tabs.TabPane key={'email'} tab={i18n('loginByEmail')} />
        </Tabs>
        {loginType === 'phone' && (
          <Form
            name="basic"
            labelCol={{ span: 0 }}
            form={phoneForm}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={submitPhone}
            autoComplete="off"
          >
            <Form.Item<FieldType>
              label=""
              name="phone"
              style={{
                marginBottom: '16px',
              }}
              rules={[
                { required: true, message: i18n('pleaseInputPhoneNumber') },
                {
                  pattern: /^1[3-9]\d{9}$/,
                  message: i18n('pleaseInputCorrectPhoneNumber'),
                },
              ]}
            >
              <Input
                addonBefore="+86"
                size="large"
                placeholder={i18n('pleaseInputPhoneNumber')}
              />
            </Form.Item>

            <Form.Item<FieldType>
              label=""
              name="password"
              style={{
                marginBottom: '16px',
              }}
              rules={[{ required: true, message: i18n('pleaseInputCode') }]}
            >
              <VerificationCodeInput
                sendSMS={sendSMS}
                placeholder={i18n('pleaseInputCode')}
              />
            </Form.Item>

            <div className="text-sm text-[#6C757D] mb-8">
              {i18n('mobileRegisterTip')}
            </div>
            <Form.Item
              style={{
                marginBottom: '16px',
              }}
            >
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                style={{
                  width: '100%',
                }}
              >
                {i18n('login')}
              </Button>
            </Form.Item>
          </Form>
        )}
        {loginType === 'email' && (
          <Form
            name="basic"
            labelCol={{ span: 0 }}
            form={emailForm}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={submitEmail}
            autoComplete="off"
          >
            <Form.Item<EmailFieldType>
              label=""
              name="email"
              validateTrigger="onBlur"
              style={{
                marginBottom: '16px',
              }}
              rules={[
                { required: true, message: i18n('pleaseInputEmail') },
                {
                  type: 'email',
                  message: i18n('pleaseInputCorrectEmail'),
                },
              ]}
            >
              <Input size="large" placeholder={i18n('pleaseInputEmail')} />
            </Form.Item>

            <Form.Item<EmailFieldType>
              label=""
              name="password"
              style={{
                marginBottom: '16px',
              }}
              rules={[{ required: true, message: i18n('pleaseInputCode') }]}
            >
              <VerificationCodeInput
                sendSMS={sendEmail}
                placeholder={i18n('pleaseInputCode')}
              />
            </Form.Item>

            <div className="text-sm text-[#6C757D] mb-8">
              {i18n('emailRegisterTip')}
            </div>
            <Form.Item
              style={{
                marginBottom: '16px',
              }}
            >
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                style={{
                  width: '100%',
                }}
              >
                {i18n('login')}
              </Button>
            </Form.Item>
          </Form>
        )}
        <Checkbox
          checked={agreeValue}
          onChange={(e) => {
            // console.log(e)
            setAgreeValue(e.target.checked);
          }}
        >
          <span className="text-xs">
            {i18n('readAgree')}
            <a
              href="/serviceAgreement"
              target="_blank"
              className="text-[#0CBF9F] hover:text-[#0CBF9F]"
            >
              {i18n('serviceAgreement')}
            </a>
            {i18n('and')}
            <a
              href="/privacyPolicy"
              target="_blank"
              className="text-[#0CBF9F] hover:text-[#0CBF9F]"
            >
              {i18n('privacyPolicy')}
            </a>
          </span>
        </Checkbox>
      </div>
    </div>
  );
};
